import { useEffect, useState } from "react";
import api from "@/lib/api";
import { downloadPdf } from "@/lib/files";
import { Button } from "@/components/ui/button";
import { FileDown, CalendarRange, Loader2 } from "lucide-react";

const MOIS = ["Janv", "Févr", "Mars", "Avr", "Mai", "Juin", "Juil", "Août", "Sept", "Oct", "Nov", "Déc"];
const nf = (n) => new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 2 }).format(n || 0);
const nf0 = (n) => new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 0 }).format(n || 0);

const Cell = ({ q, ht, bold }) => (
  <td className={`py-1.5 px-2 text-right tabular whitespace-nowrap ${bold ? "font-bold" : ""}`}>
    <div>{nf(q)}</div>
    <div className={`text-[11px] ${bold ? "text-success" : "text-muted-foreground"}`}>{nf0(ht)} DA</div>
  </td>
);

const SocieteMensuel = ({ s }) => {
  const tot = MOIS.map((_, i) => ({ q: s.rows.reduce((a, r) => a + r.mois[i].q, 0), ht: s.rows.reduce((a, r) => a + r.mois[i].ht, 0) }));
  return (
    <div className="mb-5 last:mb-0" data-testid={`mensuel-societe-${s.societe_id}`}>
      <p className="text-sm font-semibold mb-2">{s.societe}</p>
      <div className="overflow-x-auto">
        <table className="w-full text-xs min-w-[1200px]">
          <thead><tr className="text-muted-foreground border-b">
            <th className="py-2 pr-2 font-medium text-left">Produit</th>
            {MOIS.map((m) => <th key={m} className="py-2 px-2 font-medium text-right">{m}</th>)}
            <th className="py-2 px-2 font-medium text-right">Total</th>
          </tr></thead>
          <tbody className="divide-y">
            {s.rows.length === 0 && <tr><td colSpan={14} className="py-4 text-center text-muted-foreground">Aucune vente</td></tr>}
            {s.rows.map((r) => (
              <tr key={r.produit}>
                <td className="py-1.5 pr-2 min-w-[160px]">{r.produit}</td>
                {r.mois.map((m, i) => <Cell key={i} q={m.q} ht={m.ht} />)}
                <Cell q={r.q} ht={r.ht} bold />
              </tr>
            ))}
            <tr className="border-t-2 border-primary/30" data-testid={`mensuel-total-${s.societe_id}`}>
              <td className="py-1.5 pr-2 font-bold">TOTAL</td>
              {tot.map((t, i) => <Cell key={i} q={t.q} ht={t.ht} bold />)}
              <Cell q={tot.reduce((a, t) => a + t.q, 0)} ht={tot.reduce((a, t) => a + t.ht, 0)} bold />
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

export const VentesMensuellesCard = ({ societeId, exercice }) => {
  const [data, setData] = useState(null);
  const annee = exercice === "all" ? new Date().getFullYear() : exercice;
  const params = { annee, ...(societeId !== "all" && { societe_id: societeId }) };

  useEffect(() => {
    setData(null);
    api.get("/pdf/ventes-mensuelles/data", { params }).then((r) => setData(r.data)).catch(() => setData([]));
  }, [societeId, exercice]); // eslint-disable-line

  return (
    <div className="rounded-xl border bg-card p-5 mt-6" data-testid="dashboard-ventes-mensuelles">
      <div className="flex items-center justify-between gap-2 mb-1">
        <div className="flex items-center gap-2">
          <CalendarRange className="h-4 w-4 text-primary" />
          <h3 className="font-heading font-bold">Quantité &amp; valeur HT par mois <span className="text-sm font-normal text-muted-foreground">({annee})</span></h3>
        </div>
        <Button variant="outline" size="sm" className="gap-2" data-testid="ventes-mensuelles-pdf-btn"
          onClick={() => downloadPdf(`/pdf/ventes-mensuelles?${new URLSearchParams(params).toString()}`, `ventes-mensuelles-${annee}`)}>
          <FileDown className="h-4 w-4" /> PDF
        </Button>
      </div>
      <p className="text-xs text-muted-foreground mb-4">Dans chaque case : quantité vendue, puis valeur HT.</p>
      {data === null
        ? <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>
        : data.map((s) => <SocieteMensuel key={s.societe_id} s={s} />)}
    </div>
  );
};
